import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  ShieldCheck,
  Sparkles,
  Clock,
  Video,
  Zap,
} from "lucide-react";
import { API } from "../lib/api";
import { usePageTitle } from "../lib/usePageTitle";

const STEPS = [
  {
    key: "business_name",
    label: "Business name",
    hint: "So we can research you before writing anything.",
    type: "text",
    placeholder: "e.g. Sundial Coffee Roasters",
    required: true,
  },
  {
    key: "founder_name",
    label: "Your name",
    type: "text",
    placeholder: "e.g. Priya Nair",
    required: true,
  },
  {
    key: "email",
    label: "Email for delivery",
    hint: "Where your video insight lands.",
    type: "email",
    placeholder: "you@yourbusiness.com",
    required: true,
  },
  {
    key: "website",
    label: "Website (optional)",
    type: "url",
    placeholder: "https://your-business.com",
  },
  {
    key: "country",
    label: "Country / city",
    type: "text",
    placeholder: "Dubai, UAE",
  },
  {
    key: "what_you_sell",
    label: "What do you actually sell?",
    hint: "One or two lines.",
    type: "textarea",
    placeholder: "e.g. Specialty coffee subscriptions for hotels.",
    required: true,
  },
  {
    key: "who_you_sell_to",
    label: "Who buys it?",
    type: "textarea",
    placeholder: "e.g. F&B managers at 4- and 5-star hotels in the UAE and KSA.",
    required: true,
  },
  {
    key: "monthly_revenue_range",
    label: "Monthly revenue range",
    type: "choice",
    options: [
      { value: "under_5k", label: "Under $5,000 / month" },
      { value: "5k_25k", label: "$5,000 – $25,000 / month" },
      { value: "25k_100k", label: "$25,000 – $100,000 / month" },
      { value: "100k_plus", label: "$100,000+ / month" },
    ],
    required: true,
  },
  {
    key: "stuck_on",
    label: "What are you stuck on right now?",
    hint: "Be honest. Two to three lines.",
    type: "textarea",
    placeholder: "e.g. Ads are getting expensive, leads convert but churn early, my team keeps escalating everything back to me.",
    required: true,
  },
  {
    key: "tried_before",
    label: "What have you already tried?",
    hint: "Select all that apply.",
    type: "multi",
    options: [
      { value: "agencies", label: "Agencies" },
      { value: "freelancers", label: "Freelancers" },
      { value: "in_house", label: "In-house team" },
      { value: "diy", label: "DIY" },
      { value: "courses", label: "Courses & coaches" },
      { value: "nothing", label: "Nothing serious yet" },
    ],
  },
  {
    key: "dream_outcome",
    label: "If we fixed the biggest thing in 90 days, what changes?",
    hint: "This is the section Krishna writes his sharpest ideas around.",
    type: "textarea",
    placeholder: "e.g. If my team ran acquisition without me, I could scope our second city.",
    required: true,
  },
];

export default function DiagnosisPro() {
  usePageTitle("Diagnosis Pro");
  const [step, setStep] = useState(-1); // -1 is landing
  const [answers, setAnswers] = useState({
    business_name: "",
    founder_name: "",
    email: "",
    website: "",
    country: "",
    what_you_sell: "",
    who_you_sell_to: "",
    monthly_revenue_range: "",
    stuck_on: "",
    tried_before: [],
    dream_outcome: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const toggleMulti = (k, v) =>
    setAnswers((a) => {
      const list = a[k] || [];
      return { ...a, [k]: list.includes(v) ? list.filter((x) => x !== v) : [...list, v] };
    });

  const currentStep = step >= 0 ? STEPS[step] : null;
  const totalSteps = STEPS.length;

  const canAdvance = () => {
    if (!currentStep) return true;
    if (!currentStep.required) return true;
    const v = answers[currentStep.key];
    if (currentStep.type === "multi") return Array.isArray(v) && v.length > 0;
    return v && String(v).trim().length > 0;
  };

  const next = () => {
    if (!canAdvance()) {
      toast.error("Please answer this one first.");
      return;
    }
    setStep((s) => s + 1);
  };
  const back = () => setStep((s) => s - 1);

  const startCheckout = async () => {
    if (!canAdvance()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/diagnosis-pro/intake`, {
        ...answers,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        Array.isArray(detail) ? "Please check your answers." : detail || "Could not start checkout. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Landing screen ----
  if (step === -1) {
    return (
      <main
        className="relative min-h-screen bg-bg text-ink overflow-hidden"
        data-testid="diagnosis-pro-landing"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 20% 20%, rgba(201,169,97,0.14), transparent 55%), radial-gradient(circle at 82% 78%, rgba(245,220,160,0.10), transparent 60%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(201,169,97,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(201,169,97,0.35) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
          }}
        />

        <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16">
          <p className="font-sans text-[11px] uppercase tracking-[0.5em] text-brass flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-brass/60" />
            Diagnosis Pro · $499 · One-time
          </p>
          <h1
            className="mt-6"
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(1.9rem, 6vw, 5.2rem)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              fontWeight: 500,
              color: "#F5F1E8",
            }}
          >
            Know exactly what to fix,{" "}
            <span className="italic text-forest">in seven days.</span>
          </h1>
          <p className="mt-6 font-sans text-base md:text-lg text-ink-muted max-w-2xl">
            A personalised video walkthrough recorded for your business. Krishna reviews your context and records the sharpest insights you can act on. No template. No sales pitch. A conversation you can hand to your team on Monday.
          </p>

          {/* Guarantee bar */}
          <div className="mt-8 inline-flex items-start gap-3 rounded-2xl border border-brass/40 bg-brass/[0.06] px-5 py-4 max-w-2xl" data-testid="dp-guarantee-hero">
            <ShieldCheck className="h-5 w-5 text-brass shrink-0 mt-0.5" />
            <div>
              <p className="font-sans text-[11px] uppercase tracking-mega text-brass">100% refund guarantee</p>
              <p className="mt-1 font-serif text-base md:text-lg text-ink leading-snug">
                If the video is not worth the $499, we refund you. No questions asked.
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              onClick={() => setStep(0)}
              data-testid="dp-start"
              className="rounded-full bg-forest px-8 py-4 font-sans text-xs uppercase tracking-mega text-bg hover:bg-forest-hover transition-colors inline-flex items-center gap-2 group"
            >
              Start your diagnosis
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <span className="font-sans text-xs uppercase tracking-mega text-ink-muted">
              $499 · pay once · video delivered in 7 days
            </span>
          </div>
        </section>

        {/* What you get */}
        <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-sans text-xs uppercase tracking-mega text-brass mb-6">Inside the video</p>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Video, title: "Personalised video", body: "Recorded for your business by name. Not a template. Not a template read aloud." },
              { icon: Sparkles, title: "The one bottleneck", body: "The single thing stopping your growth. Named plainly, on camera." },
              { icon: Zap, title: "Stack audit", body: "What to adopt. What to drop. Real tools, not brand names." },
              { icon: Clock, title: "90-day plan", body: "Days 1-30. 31-60. 61-90. Talked through, not bullet dust." },
              { icon: ShieldCheck, title: "Kill list", body: "Three things to stop doing today, said out loud so your team hears it too." },
              { icon: Check, title: "Metrics that matter", body: "Five KPIs to watch weekly. Specific to your business." },
            ].map((c) => (
              <div key={c.title} className="rounded-2xl border border-line bg-surface p-6">
                <c.icon className="h-5 w-5 text-brass mb-4" />
                <h3 className="font-serif text-xl text-ink">{c.title}</h3>
                <p className="mt-2 font-sans text-sm text-ink-muted">{c.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Compared to alternatives */}
        <section className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="font-sans text-xs uppercase tracking-mega text-brass mb-6">
            Why $499 is the right price
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                alt: "A senior consultant call",
                cost: "$500 - $1,000 / hour",
                gap: "You leave with notes, not a recording you can replay.",
              },
              {
                alt: "A free agency audit",
                cost: "Free",
                gap: "You leave with a sales pitch, not a plan.",
              },
              {
                alt: "Diagnosis Pro",
                cost: "$499 · once",
                gap: "You leave with a personalised video plan, backed by a 100% refund guarantee.",
                us: true,
              },
            ].map((r) => (
              <div
                key={r.alt}
                className={`rounded-2xl p-6 ${r.us ? "bg-ink text-bg" : "bg-surface border border-line"}`}
              >
                <p className={`font-sans text-[10px] uppercase tracking-mega ${r.us ? "text-brass" : "text-ink-muted"}`}>
                  {r.us ? "This report" : r.alt}
                </p>
                <p className={`mt-2 font-serif text-xl ${r.us ? "text-bg" : "text-ink"}`}>
                  {r.us ? r.alt : r.cost}
                </p>
                <p className={`mt-4 font-sans text-sm ${r.us ? "text-bg/75" : "text-ink-muted"}`}>
                  {r.gap}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Big CTA */}
        <section className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24 text-center">
          <h2
            className="italic"
            style={{
              fontFamily: "'Fraunces', serif",
              fontSize: "clamp(1.9rem, 6vw, 5.2rem)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              fontWeight: 500,
              color: "#F5F1E8",
            }}
          >
            Ready when you are.
          </h2>
          <p className="mt-4 font-sans text-base md:text-lg text-ink-muted">
            Answer eleven questions. Pay $499. Get your personalised video inside seven days. Not worth it? Full refund, no questions asked.
          </p>
          <button
            onClick={() => setStep(0)}
            data-testid="dp-start-2"
            className="mt-8 rounded-full bg-forest px-8 py-4 font-sans text-xs uppercase tracking-mega text-bg hover:bg-forest-hover transition-colors inline-flex items-center gap-2 group"
          >
            Start your diagnosis
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Guarantee reinforcement */}
          <div className="mt-10 mx-auto max-w-xl rounded-2xl border border-brass/40 bg-brass/[0.05] px-6 py-5 flex items-start gap-3 text-left" data-testid="dp-guarantee-footer">
            <ShieldCheck className="h-5 w-5 text-brass shrink-0 mt-0.5" />
            <p className="font-serif text-base md:text-lg text-ink leading-snug">
              <span className="font-sans text-[10px] uppercase tracking-mega text-brass block mb-1">100% refund guarantee</span>
              Watch the video. If it is not worth the $499, tell us within seven days and we refund you in full. No questions asked.
            </p>
          </div>

          <div className="mt-14">
            <p className="font-serif text-2xl md:text-4xl text-ink">
              Growth Engine{" "}
              <span className="italic text-forest">with Peace of Mind.</span>
            </p>
          </div>
          <div className="mt-8">
            <Link
              to="/diagnose"
              data-testid="dp-back-to-doors"
              className="font-sans text-xs uppercase tracking-mega text-ink-muted hover:text-forest transition-colors"
            >
              Or explore the two doors &rarr;
            </Link>
          </div>
        </section>
      </main>
    );
  }

  // ---- Form step ----
  const q = currentStep;
  const isLast = step === STEPS.length - 1;
  const progress = ((step + 1) / totalSteps) * 100;

  return (
    <main
      className="min-h-screen bg-bg text-ink pt-24 md:pt-28 pb-24 px-6 md:px-12 relative overflow-hidden"
      data-testid="diagnosis-pro-form"
    >
      {/* Champagne atmospherics */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-16 h-[620px] w-[620px] rounded-full opacity-[0.10] blur-[120px]"
        style={{ background: "radial-gradient(circle, rgba(201,169,97,0.9) 0%, rgba(201,169,97,0) 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 bottom-0 h-[520px] w-[520px] rounded-full opacity-[0.08] blur-[110px]"
        style={{ background: "radial-gradient(circle, rgba(245,220,160,0.75) 0%, rgba(245,220,160,0) 70%)" }}
      />
      <div className="max-w-3xl mx-auto relative z-10">
        {/* Progress */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setStep(-1)}
              data-testid="dp-back-to-landing"
              className="font-sans text-[11px] uppercase tracking-mega text-ink-muted hover:text-forest transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="h-3 w-3" />
              Overview
            </button>
            <span className="font-sans text-[11px] uppercase tracking-mega text-brass">
              Question {step + 1} of {totalSteps}
            </span>
          </div>
          <div className="h-[2px] bg-line/70 overflow-hidden rounded-full">
            <motion.div
              className="h-full bg-forest"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={q.key}
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -32 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`dp-step-${q.key}`}
          >
            <p className="font-sans text-[11px] uppercase tracking-mega text-brass mb-5">
              {q.required ? "Required" : "Optional"}
            </p>
            <h2
              style={{
                fontFamily: "'Fraunces', serif",
                fontSize: "clamp(1.9rem, 6vw, 5.2rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.02em",
                fontWeight: 500,
                color: "#F5F1E8",
              }}
            >
              {q.label}
            </h2>
            {q.hint && (
              <p className="mt-6 font-serif italic text-lg md:text-xl text-ink-muted leading-relaxed">
                {q.hint}
              </p>
            )}
            <div className="mt-10 md:mt-12">
              {(q.type === "text" || q.type === "email" || q.type === "url") && (
                <input
                  type={q.type}
                  value={answers[q.key]}
                  onChange={(e) => set(q.key, e.target.value)}
                  placeholder={q.placeholder}
                  autoFocus
                  data-testid={`dp-input-${q.key}`}
                  className="w-full bg-transparent border-b-2 border-line py-4 font-serif text-2xl md:text-3xl text-ink placeholder:text-ink-muted/40 outline-none focus:border-forest transition-colors"
                />
              )}
              {q.type === "textarea" && (
                <textarea
                  value={answers[q.key]}
                  onChange={(e) => set(q.key, e.target.value)}
                  placeholder={q.placeholder}
                  autoFocus
                  rows={5}
                  data-testid={`dp-input-${q.key}`}
                  className="w-full bg-transparent border-b-2 border-line py-4 font-serif text-xl md:text-2xl text-ink placeholder:text-ink-muted/40 outline-none focus:border-forest transition-colors resize-none leading-relaxed"
                />
              )}
              {q.type === "choice" && (
                <div className="space-y-4">
                  {q.options.map((opt) => {
                    const active = answers[q.key] === opt.value;
                    return (
                      <motion.button
                        key={opt.value}
                        onClick={() => set(q.key, opt.value)}
                        data-testid={`dp-choice-${q.key}-${opt.value}`}
                        whileHover={{ x: 4 }}
                        transition={{ duration: 0.25 }}
                        className={`w-full text-left rounded-2xl border-2 p-6 md:p-7 transition-all duration-300 ${
                          active
                            ? "border-forest bg-forest/5 shadow-[0_2px_20px_-8px_rgba(43,74,59,0.35)]"
                            : "border-line bg-surface hover:border-forest/60"
                        }`}
                      >
                        <div className="flex items-center gap-5">
                          <span
                            className={`inline-flex items-center justify-center h-7 w-7 rounded-full border-2 shrink-0 transition-colors ${
                              active ? "bg-forest border-forest" : "border-line"
                            }`}
                          >
                            {active && <Check className="h-4 w-4 text-bg" />}
                          </span>
                          <span className="font-serif text-xl md:text-2xl text-ink leading-tight">
                            {opt.label}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
              {q.type === "multi" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {q.options.map((opt) => {
                    const active = (answers[q.key] || []).includes(opt.value);
                    return (
                      <motion.button
                        key={opt.value}
                        onClick={() => toggleMulti(q.key, opt.value)}
                        data-testid={`dp-multi-${q.key}-${opt.value}`}
                        whileHover={{ y: -2 }}
                        transition={{ duration: 0.25 }}
                        className={`text-left rounded-2xl border-2 p-5 md:p-6 transition-all duration-300 ${
                          active
                            ? "border-forest bg-forest/5 shadow-[0_2px_20px_-8px_rgba(43,74,59,0.35)]"
                            : "border-line bg-surface hover:border-forest/60"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span
                            className={`inline-flex items-center justify-center h-6 w-6 rounded border-2 shrink-0 transition-colors ${
                              active ? "bg-forest border-forest" : "border-line"
                            }`}
                          >
                            {active && <Check className="h-3.5 w-3.5 text-bg" />}
                          </span>
                          <span className="font-serif text-lg md:text-xl text-ink leading-tight">
                            {opt.label}
                          </span>
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-14 flex items-center justify-between">
              <button
                onClick={back}
                disabled={step === 0}
                data-testid="dp-back-btn"
                className="font-sans text-xs uppercase tracking-mega text-ink-muted hover:text-forest transition-colors flex items-center gap-2 disabled:opacity-30"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              {isLast ? (
                <button
                  onClick={startCheckout}
                  disabled={submitting || !canAdvance()}
                  data-testid="dp-checkout-btn"
                  className="rounded-full bg-forest px-8 py-4 font-sans text-sm text-bg hover:bg-forest-hover transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? "Redirecting..." : "Pay $499 and get your video"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={next}
                  disabled={!canAdvance()}
                  data-testid="dp-next-btn"
                  className="rounded-full bg-forest px-8 py-4 font-sans text-sm text-bg hover:bg-forest-hover transition-colors flex items-center gap-2 disabled:opacity-40"
                >
                  Next
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </main>
  );
}
